import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Square, Mic } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useBluetoothButton } from '../hooks/useBluetoothButton';
import { bluetoothButtonService } from '../services/bluetoothButtonService';
import { useMediaButton } from '../hooks/useMediaButton';
import { mediaButtonService } from '../services/mediaButtonService';
import { useWakeLock } from '../hooks/useWakeLock';
import { useVoiceStopTrigger } from '../hooks/useVoiceStopTrigger';
import { voiceTriggerService } from '../services/voiceTriggerService';
import AppBarMenu from './AppBarMenu';
import Logo from './Logo';
import Audiogram from './Audiogram';
import { getOrCreateInspection, processAndUploadSegment, getSegmentStats } from '../services/segmentService';
import { storeChunk } from '../services/chunkStorage';
import { getSplitStreams, cleanup as cleanupAudioStream, getAudioContext, getSourceNode } from '../services/audioStreamService';
import { getInputDeviceName } from '../services/microphoneService';
import { useVoskVoiceTrigger, shouldUseVosk } from '../hooks/useVoskVoiceTrigger';
import { trimAudioEnd, trimAudioStart } from '../services/audioProcessingService';
import { settings } from '../services/settingsService';
import { useExtendedErrorReportsSetting } from '../hooks/useSetting';

// Vibration patterns
const vibrateStart = () => {
  if (settings.vibrateOnRecording.isEnabled() && navigator.vibrate) {
    navigator.vibrate(200); // Single short vibration
  }
};

const vibrateError = () => {
  if (settings.vibrateOnRecording.isEnabled() && navigator.vibrate) {
    navigator.vibrate([100, 100, 100, 100, 100]); // 3 short vibrations with pauses
  }
};

// Alert component
const Alert = ({ severity, children }) => {
  const styles = {
    error: 'bg-red-50 border-red-200 text-red-800',
    warning: 'bg-amber-50 border-amber-200 text-amber-800',
    success: 'bg-green-50 border-green-200 text-green-800',
    info: 'bg-blue-50 border-blue-200 text-blue-800',
  };

  return (
    <div className={`px-4 py-3 border rounded-lg ${styles[severity]}`}>
      {children}
    </div>
  );
};

const Recording = ({ vehicleId, deviceId = null, startTriggerInfo = null, onStopRecording, onCancel, onHome, onLogout, initialElapsedTime = 0, isAdmin = false }) => {
  const { t } = useTranslation();
  const [segmentTime, setSegmentTime] = useState(0); // This segment's duration only
  const displayTime = initialElapsedTime + segmentTime; // Total time shown to user
  const [error, setError] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [analyser, setAnalyser] = useState(null);
  const [bluetoothButtonEnabled, setBluetoothButtonEnabled] = useState(bluetoothButtonService.isEnabled());
  const [mediaButtonEnabled, setMediaButtonEnabled] = useState(mediaButtonService.isEnabled());
  const [stopTriggerEnabled, setStopTriggerEnabled] = useState(voiceTriggerService.isStopEnabled());
  const [inputDeviceName, setInputDeviceName] = useState(null);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerIntervalRef = useRef(null);
  const streamRef = useRef(null);
  const mimeTypeRef = useRef('audio/webm');
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);
  const inspectionRef = useRef(null);
  const isInitializingRef = useRef(false); // Prevent double initialization in StrictMode
  const recordingStartTimeRef = useRef(null); // For voice stop timestamp calculation
  const isStoppingRef = useRef(false); // Prevent double-stop from voice trigger + manual
  const recognitionStreamRef = useRef(null); // Stream for speech recognition
  const chunkTimerRef = useRef(null); // Timer for auto-chunking
  const isAutoChunkingRef = useRef(false); // Flag to indicate auto-chunk in progress
  const chunkStartTimeRef = useRef(null); // Track when current chunk started (for accurate duration)
  const segmentTimeRef = useRef(0); // Track segment time in ref to avoid stale closures
  const handleStopRecordingRef = useRef(null); // Ref for handleStopRecording to avoid stale closure in voice callback
  const pendingTrimMsRef = useRef(null); // Ref to store trim value for voice stop (read by onstop handler)
  const voiceStopInfoRef = useRef(null); // Ref to store voice stop detection info for calculating trim in onstop handler
  const [debugInfo, setDebugInfo] = useState(null); // On-screen debug for mobile
  const [extendedErrorReportsEnabled] = useExtendedErrorReportsSetting();
  const [voiceDebugLog, setVoiceDebugLog] = useState([]); // Voice recognition debug log
  const voiceDebugIdRef = useRef(0);

  // Vosk-related state (Android only)
  const useVoskForStopTrigger = shouldUseVosk();
  const [voskAudioContext, setVoskAudioContext] = useState(null);
  const [voskSourceNode, setVoskSourceNode] = useState(null);
  const [voskModelLoadProgress, setVoskModelLoadProgress] = useState(null);

  // Keep segmentTimeRef in sync with segmentTime state
  useEffect(() => {
    segmentTimeRef.current = segmentTime;
  }, [segmentTime]);

  // Wake lock to keep screen awake during recording
  const { isSupported: wakeLockSupported, isLocked: wakeLockActive, error: wakeLockError } = useWakeLock(isRecording);

  // Log wake lock status changes
  useEffect(() => {
    if (wakeLockSupported && isRecording) {
      if (wakeLockActive) {
        console.log('✓ Screen will stay awake during recording');
      } else if (wakeLockError) {
        console.warn('Wake Lock error:', wakeLockError);
      }
    }
  }, [wakeLockSupported, wakeLockActive, wakeLockError, isRecording]);

  // Listen for Bluetooth button setting changes
  useEffect(() => {
    const handleBluetoothButtonSettingChange = (event) => {
      const { enabled } = event.detail;
      setBluetoothButtonEnabled(enabled);
    };

    window.addEventListener('bluetoothButtonSettingChanged', handleBluetoothButtonSettingChange);
    return () => window.removeEventListener('bluetoothButtonSettingChanged', handleBluetoothButtonSettingChange);
  }, []);

  // Listen for media button setting changes
  useEffect(() => {
    const handleMediaButtonSettingChange = (event) => {
      const { enabled } = event.detail;
      setMediaButtonEnabled(enabled);
    };

    window.addEventListener('mediaButtonSettingChanged', handleMediaButtonSettingChange);
    return () => window.removeEventListener('mediaButtonSettingChanged', handleMediaButtonSettingChange);
  }, []);

  // Bluetooth button detection hook
  useBluetoothButton(
    bluetoothButtonEnabled && isRecording,
    () => {
      console.log('Bluetooth button pressed during recording - stopping');
      handleStopRecording();
    }
  );

  // Media button (headset play/pause) detection hook
  useMediaButton(
    mediaButtonEnabled && isRecording,
    () => {
      console.log('Media button pressed during recording - stopping');
      handleStopRecording();
    }
  );

  // Listen for stop trigger setting changes
  useEffect(() => {
    const handleStopTriggerSettingChange = (event) => {
      const { enabled } = event.detail;
      setStopTriggerEnabled(enabled);
    };

    window.addEventListener('stopTriggerSettingChanged', handleStopTriggerSettingChange);
    return () => window.removeEventListener('stopTriggerSettingChanged', handleStopTriggerSettingChange);
  }, []);

  // Handle voice stop trigger with timestamp for audio trimming
  const handleVoiceStopDetected = useCallback((detectionTimestamp, matchedPhrase = null) => {
    if (isStoppingRef.current) return;
    console.log('Voice stop trigger detected - stopping recording with trim');
    setDebugInfo(prev => ({ ...prev, callbackCalled: true, callbackTime: Date.now() }));

    // Store all info needed for trim calculation in a ref (most reliable way to pass data to onstop handler)
    const detectedPhrase = matchedPhrase || voiceTriggerService.getStopWord();
    voiceStopInfoRef.current = {
      detectionTimestamp,
      detectedPhrase,
      recordingStartTime: recordingStartTimeRef.current
    };
    console.log(`[VoiceStop] Stored voiceStopInfo:`, JSON.stringify(voiceStopInfoRef.current));

    // Also calculate and store the trim value directly (belt and suspenders)
    const stopPhraseBufferMs = voiceTriggerService.estimatePhraseDurationMs(detectedPhrase);
    const EXTRA_BUFFER_MS = 1500;
    const keepDurationMs = detectionTimestamp - recordingStartTimeRef.current - stopPhraseBufferMs - EXTRA_BUFFER_MS;
    const trimValue = keepDurationMs > 500 ? keepDurationMs : null;
    pendingTrimMsRef.current = trimValue;

    console.log(`[VoiceStop] Calculated trimValue=${trimValue} (keepDurationMs=${keepDurationMs})`);

    // Call handleStopRecording (may or may not receive the parameter correctly on mobile)
    if (handleStopRecordingRef.current) {
      handleStopRecordingRef.current(trimValue);
    } else {
      console.error('[VoiceStop] handleStopRecordingRef.current is null!');
    }
  }, []);

  // Handle fatal voice recognition error
  const handleVoiceFatalError = useCallback(() => {
    console.warn('Voice stop trigger disabled due to fatal error');
    setStopTriggerEnabled(false);
  }, []);

  // Debug callback for hook (shows on-screen debug info on mobile)
  const handleHookDebug = useCallback((info) => {
    setDebugInfo(prev => ({ ...prev, ...info }));

    // Add transcript entries to voice debug log when Extended Error Reports enabled
    if (extendedErrorReportsEnabled && info.type === 'transcript') {
      const timestamp = new Date().toLocaleTimeString('de-DE', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        fractionalSecondDigits: 3
      });
      voiceDebugIdRef.current += 1;
      const entry = {
        id: `${Date.now()}-${voiceDebugIdRef.current}`,
        timestamp,
        ...info
      };
      setVoiceDebugLog(prev => [entry, ...prev].slice(0, 100));
    }
  }, [extendedErrorReportsEnabled]);

  // Voice stop trigger detection hook - now uses Web Audio API fan-out pattern
  // Pass voiceStopInfoRef so hook can write stop info SYNCHRONOUSLY (critical for mobile)
  // Only use on non-Android platforms (Android uses Vosk)
  const { isListening: webSpeechStopListening, lastHeardText: voiceStopHeardText, retryCount: voiceRetryCount } = useVoiceStopTrigger({
    enabled: stopTriggerEnabled && isRecording && !useVoskForStopTrigger,
    stream: recognitionStreamRef.current,
    onStopDetected: handleVoiceStopDetected,
    onFatalError: handleVoiceFatalError,
    voiceStopInfoRef,
    onDebug: handleHookDebug
  });

  // Capture audioContext and sourceNode from audioStreamService for Vosk (Android only)
  useEffect(() => {
    if (!useVoskForStopTrigger || !isRecording) {
      return;
    }

    // Poll for audioContext/sourceNode to be available (set by getSplitStreams)
    const checkInterval = setInterval(() => {
      const ctx = getAudioContext();
      const src = getSourceNode();
      if (ctx && src) {
        console.log('[Recording/Vosk] AudioContext and sourceNode acquired from audioStreamService');
        setVoskAudioContext(ctx);
        setVoskSourceNode(src);
        clearInterval(checkInterval);
      }
    }, 100);

    return () => {
      clearInterval(checkInterval);
      setVoskAudioContext(null);
      setVoskSourceNode(null);
    };
  }, [useVoskForStopTrigger, isRecording]);

  // Handle Vosk keyword detection for stop trigger
  const handleVoskStopDetected = useCallback((keyword, timestamp) => {
    console.log('[Recording/Vosk] Stop keyword detected:', keyword);
    handleVoiceStopDetected(timestamp, keyword);
  }, [handleVoiceStopDetected]);

  // Vosk voice stop trigger hook (Android only)
  const {
    isListening: voskStopListening,
    // isModelReady: isVoskStopModelReady, // May be used for future UI enhancement
    isModelLoading: isVoskStopModelLoading,
    // lastHeardText: voskStopLastHeardText, // Transcript is handled via onTranscript callback
    // error: voskStopError // Error handling via onError callback
  } = useVoskVoiceTrigger({
    enabled: useVoskForStopTrigger && stopTriggerEnabled && isRecording && voskAudioContext !== null,
    audioContext: voskAudioContext,
    sourceNode: voskSourceNode,
    keywords: voiceTriggerService.getStopKeywords(),
    onKeywordDetected: handleVoskStopDetected,
    onModelLoadProgress: setVoskModelLoadProgress,
    onTranscript: (text, isFinal) => {
      handleHookDebug({
        type: 'transcript',
        transcript: text,
        isFinal
      });
    },
    onError: (err) => {
      console.error('[Recording/Vosk] Stop trigger error:', err);
      handleHookDebug({ type: 'error', error: err.message });
    }
  });

  // Combine Web Speech API and Vosk listening states
  const voiceStopListening = useVoskForStopTrigger ? voskStopListening : webSpeechStopListening;
  const isVoiceStopModelLoading = useVoskForStopTrigger ? isVoskStopModelLoading : false;

  // Track if we've already started in this component instance (survives StrictMode remount)
  const hasStartedRef = useRef(false);

  useEffect(() => {
    // Prevent double-start in React 18 StrictMode (effect runs twice)
    if (hasStartedRef.current) {
      console.log('[Recording] useEffect skipped - already started in this instance');
      return;
    }
    hasStartedRef.current = true;

    startRecording();

    return () => {
      isInitializingRef.current = false; // Reset for potential remount
      isStoppingRef.current = false; // Reset to allow stop on next recording
      isAutoChunkingRef.current = false; // Reset auto-chunk flag
      stopTimer();
      // Clear chunk timer on unmount
      if (chunkTimerRef.current) {
        clearTimeout(chunkTimerRef.current);
        chunkTimerRef.current = null;
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      stopMediaStream();
      stopAudioAnalysis();
    };
  }, []);

  // Returns a Promise that resolves when the beep finishes playing
  // On mobile, audio playback has latency, so we wait for the beep to complete
  // before starting the MediaRecorder to avoid recording the beep
  const playStartBeep = () => {
    return new Promise((resolve) => {
      const volume = (settings.beepVolume.get() / 100) * 2; // Convert 0-100 to 0-2 (2x louder at max)
      if (volume === 0) {
        resolve(); // Skip if muted
        return;
      }

      try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        oscillator.frequency.value = 800;
        oscillator.type = 'sine';

        // Beep duration: 150ms
        const beepDuration = 0.15;
        gainNode.gain.setValueAtTime(0, audioContext.currentTime);
        gainNode.gain.linearRampToValueAtTime(volume, audioContext.currentTime + 0.01);
        gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + beepDuration);

        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + beepDuration);

        oscillator.onended = () => {
          audioContext.close().catch(() => {});
          // Add buffer for mobile audio latency (beep schedules but playback is delayed)
          // 300ms accounts for typical mobile audio output latency
          setTimeout(resolve, 300);
        };

        // Fallback: resolve after max beep duration + buffer (prevents hang if onended doesn't fire)
        setTimeout(() => {
          if (audioContext.state !== 'closed') {
            audioContext.close().catch(() => {});
          }
          resolve();
        }, 600);
      } catch (error) {
        console.error('Error playing start beep:', error);
        resolve(); // Resolve anyway to not block recording
      }
    });
  };

  const playStopBeep = () => {
    const volume = (settings.beepVolume.get() / 100) * 2; // Convert 0-100 to 0-2 (2x louder at max)
    if (volume === 0) return; // Skip if muted

    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = 600;
      oscillator.type = 'sine';

      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(volume, audioContext.currentTime + 0.01);
      gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.2);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.2);

      oscillator.onended = () => audioContext.close().catch(() => {});

      // Fallback: close after max beep duration + buffer (prevents AudioContext leak if onended doesn't fire)
      setTimeout(() => {
        if (audioContext.state !== 'closed') {
          audioContext.close().catch(() => {});
        }
      }, 500);
    } catch (error) {
      console.error('Error playing stop beep:', error);
    }
  };

  const startRecording = async () => {
    console.log('[Recording] startRecording entered. isInitializing:', isInitializingRef.current, 'isRecording:', isRecording);

    // Prevent double initialization (can happen with React StrictMode)
    if (isInitializingRef.current || isRecording) {
      console.log('[Recording] startRecording skipped - already initializing or recording');
      return;
    }
    isInitializingRef.current = true;
    isStoppingRef.current = false;
    isAutoChunkingRef.current = false;

    // Reset timer to 0 when starting a new recording session
    setSegmentTime(0);

    console.log('[Recording] startRecording proceeding with:', { vehicleId, deviceId: deviceId || '(default/null)' });

    try {
      // Use audio stream service to get split streams for recording + speech recognition
      console.log('[Recording] Getting audio streams...');
      const { recordingStream, recognitionStream } = await getSplitStreams(deviceId);
      console.log('[Recording] Audio streams acquired');
      streamRef.current = recordingStream;
      recognitionStreamRef.current = recognitionStream;

      // Create inspection at recording start
      try {
        // Extract title from startTriggerInfo (passed from VehicleInput)
        const title = startTriggerInfo?.title || null;
        console.log('[Recording] Creating inspection for vehicleId:', vehicleId, 'title:', title);
        const inspectionId = await getOrCreateInspection(vehicleId, title);
        inspectionRef.current = { inspectionId, vehicleId, title };
        console.log('[Recording] Inspection ready:', inspectionId);

        // Update URL to include inspection ID (without triggering React Router navigation)
        // This allows page refresh to restore the session
        if (inspectionId && !window.location.pathname.includes(inspectionId)) {
          window.history.replaceState(null, '', `/record/${inspectionId}`);
          console.log('[Recording] URL updated to:', `/record/${inspectionId}`);
        }
      } catch (err) {
        console.error('[Recording] Failed to create inspection:', err);
        setError('Failed to initialize recording session');
        stopMediaStream();
        return;
      }

      let mimeType = 'audio/webm';
      if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        mimeType = 'audio/webm;codecs=opus';
      } else if (MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')) {
        mimeType = 'audio/ogg;codecs=opus';
      } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
        mimeType = 'audio/mp4';
      }

      mimeTypeRef.current = mimeType;

      const mediaRecorder = new MediaRecorder(recordingStream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      // Handle unexpected recording errors
      mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event.error);
        vibrateError();
        setError(t('recording.error_recording_failed'));
      };

      // Listen for track ended (e.g., microphone disconnected)
      recordingStream.getTracks().forEach(track => {
        track.onended = () => {
          if (isRecording && !isStoppingRef.current) {
            console.warn('Recording track ended unexpectedly');
            vibrateError();
          }
        };
      });

      // Play beep BEFORE starting MediaRecorder to avoid recording the beep
      // This is especially important on mobile where audio playback has latency
      // The beep would otherwise play 500-1000ms into the recording
      await playStartBeep();
      vibrateStart();

      mediaRecorder.start();
      console.log('[Recording] MediaRecorder started, state:', mediaRecorder.state);
      setIsRecording(true);
      setInputDeviceName(getInputDeviceName()); // Get current input device name
      recordingStartTimeRef.current = Date.now(); // Capture start time for voice stop trimming
      chunkStartTimeRef.current = Date.now(); // Track chunk start for accurate duration

      startTimer();
      setupAudioAnalysis(recordingStream);

      // Start auto-chunk timer
      startChunkTimer();

    } catch (err) {
      console.error('Error starting recording:', err);
      isInitializingRef.current = false; // Reset on error

      if (err.name === 'NotAllowedError') {
        // Detect platform and browser for appropriate hint
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        const isAndroid = /Android/.test(navigator.userAgent);

        // Detect browser name
        const getBrowserName = () => {
          const ua = navigator.userAgent;
          if (ua.includes('CriOS') || (ua.includes('Chrome') && !ua.includes('Edg'))) return 'Chrome';
          if (ua.includes('FxiOS') || ua.includes('Firefox')) return 'Firefox';
          if (ua.includes('Edg')) return 'Edge';
          if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari';
          return 'Browser';
        };

        let hint;
        if (isIOS) {
          const browser = getBrowserName();
          hint = t('recording.error_mic_denied_hint_ios', { browser });
        } else if (isAndroid) {
          hint = t('recording.error_mic_denied_hint_android');
        } else {
          hint = t('recording.error_mic_denied_hint_desktop');
        }
        setError(`${t('recording.error_mic_denied')} ${hint}`);
      } else if (err.name === 'NotFoundError') {
        setError(t('recording.error_mic_not_found'));
      } else {
        setError(t('recording.error_recording_failed'));
      }
    }
  };

  const startTimer = () => {
    // Clear any existing interval first to prevent duplicates
    // (can happen with React StrictMode double-mounting)
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }
    timerIntervalRef.current = setInterval(() => {
      setSegmentTime((prev) => prev + 1);
    }, 1000);
  };

  const stopTimer = () => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
  };

  const stopMediaStream = () => {
    // Cleanup audio stream service (closes AudioContext, disconnects nodes)
    cleanupAudioStream();

    // Clear our references
    streamRef.current = null;
    recognitionStreamRef.current = null;
  };

  const setupAudioAnalysis = (stream) => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;

      const newAnalyser = audioContext.createAnalyser();
      newAnalyser.fftSize = 512;
      newAnalyser.smoothingTimeConstant = 0.7;
      analyserRef.current = newAnalyser;
      setAnalyser(newAnalyser);

      const source = audioContext.createMediaStreamSource(stream);
      source.connect(newAnalyser);

      analyzeAudioLevel();
    } catch (err) {
      console.error('Error setting up audio analysis:', err);
    }
  };

  const analyzeAudioLevel = () => {
    if (!analyserRef.current) return;

    const analyser = analyserRef.current;
    const dataArray = new Uint8Array(analyser.frequencyBinCount);

    const analyze = () => {
      analyser.getByteFrequencyData(dataArray);
      const average = dataArray.reduce((sum, value) => sum + value, 0) / dataArray.length;
      const normalizedLevel = Math.min((average / 255) * 2.5, 1);
      setAudioLevel(normalizedLevel);
      animationFrameRef.current = requestAnimationFrame(analyze);
    };

    analyze();
  };

  const stopAudioAnalysis = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    analyserRef.current = null;
    setAudioLevel(0);
  };

  // Start chunk timer based on settings
  const startChunkTimer = () => {
    const chunkDurationMs = settings.chunkDuration.get() * 1000;
    console.log(`[ChunkTimer] Starting chunk timer for ${chunkDurationMs}ms (${settings.chunkDuration.get()}s)`);

    chunkTimerRef.current = setTimeout(() => {
      console.log('[ChunkTimer] Chunk duration reached, triggering auto-chunk');
      handleAutoChunk();
    }, chunkDurationMs);
  };

  // Stop chunk timer
  const stopChunkTimer = () => {
    if (chunkTimerRef.current) {
      clearTimeout(chunkTimerRef.current);
      chunkTimerRef.current = null;
      console.log('[ChunkTimer] Chunk timer cleared');
    }
  };

  // Handle automatic chunk split (stop recorder, upload chunk, restart recorder)
  const handleAutoChunk = async () => {
    if (!mediaRecorderRef.current || mediaRecorderRef.current.state === 'inactive') {
      console.log('[AutoChunk] MediaRecorder not active, skipping');
      return;
    }

    console.log('[AutoChunk] Starting auto-chunk process');
    isAutoChunkingRef.current = true;

    const currentInspection = inspectionRef.current;
    const mimeType = mimeTypeRef.current;
    // Calculate chunk duration from chunk start time (more accurate than segment timer)
    const chunkDuration = chunkStartTimeRef.current
      ? Math.round((Date.now() - chunkStartTimeRef.current) / 1000)
      : segmentTimeRef.current;
    const chunkTimestamp = Date.now();

    // Set up handler for when MediaRecorder stops
    mediaRecorderRef.current.addEventListener('stop', async () => {
      const rawBlob = new Blob(audioChunksRef.current, { type: mimeType });
      audioChunksRef.current = []; // Clear for next chunk

      if (rawBlob.size === 0) {
        console.log('[AutoChunk] Empty chunk, skipping');
        isAutoChunkingRef.current = false;
        return;
      }

      console.log(`[AutoChunk] Chunk ready: ${rawBlob.size} bytes, ${chunkDuration}s`);

      // Store chunk to IndexedDB and upload in background (skip if zero-duration)
      if (chunkDuration > 0 && currentInspection) {
        const stats = await getSegmentStats(currentInspection.inspectionId);
        const chunkIndex = stats.total;
        console.log(`[AutoChunk] Creating chunk index ${chunkIndex} for inspection ${currentInspection.inspectionId}`);

        const pendingChunkId = await storeChunk({
          recordingId: currentInspection.inspectionId,
          chunkIndex,
          blob: rawBlob,
          mimeType: mimeType || 'audio/webm',
          duration: chunkDuration,
          timestamp: chunkTimestamp
        });
        console.log('[AutoChunk] Chunk stored to IndexedDB:', pendingChunkId);

        // Upload in background (no trimming for auto-chunks)
        processAndUploadSegment(
          currentInspection.inspectionId,
          rawBlob,
          (status) => console.log('[AutoChunk] Upload status:', status),
          pendingChunkId
        ).then(result => {
          console.log('[AutoChunk] Upload complete:', result.filename);
        }).catch(err => {
          console.error('[AutoChunk] Upload failed:', err);
        });
      }

      // Start new MediaRecorder for next chunk
      try {
        console.log('[AutoChunk] Creating new MediaRecorder, stream active:', streamRef.current?.active, 'tracks:', streamRef.current?.getTracks().map(t => ({ kind: t.kind, readyState: t.readyState })));
        const newMediaRecorder = new MediaRecorder(streamRef.current, { mimeType });
        mediaRecorderRef.current = newMediaRecorder;

        newMediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            audioChunksRef.current.push(event.data);
          }
        };

        newMediaRecorder.onerror = (event) => {
          console.error('MediaRecorder error:', event.error);
          vibrateError();
          setError(t('recording.error_recording_failed'));
        };

        newMediaRecorder.start();
        console.log('[AutoChunk] New MediaRecorder started, state:', newMediaRecorder.state);

        // Reset chunk start time for next chunk duration calculation
        // NOTE: Don't reset segmentTime - keep the display timer running continuously
        chunkStartTimeRef.current = Date.now();

        // Update recording start time for voice stop calculations
        recordingStartTimeRef.current = Date.now();

        // Restart chunk timer
        startChunkTimer();

        isAutoChunkingRef.current = false;
      } catch (err) {
        console.error('[AutoChunk] Failed to restart MediaRecorder:', err);
        isAutoChunkingRef.current = false;
        // If restart fails, stop recording entirely
        handleStopRecording();
      }
    }, { once: true });

    // Stop current recorder (triggers the 'stop' handler above)
    mediaRecorderRef.current.stop();
  };

  const handleStopRecording = async (trimToMs = null) => {
    console.log(`[handleStopRecording] Called with trimToMs=${trimToMs}, isAutoChunking=${isAutoChunkingRef.current}`);

    // If auto-chunking is in progress, wait for it to complete
    if (isAutoChunkingRef.current) {
      console.log('[handleStopRecording] Waiting for auto-chunk to complete...');
      // Wait up to 5 seconds for auto-chunk to finish
      let waitCount = 0;
      while (isAutoChunkingRef.current && waitCount < 50) {
        await new Promise(resolve => setTimeout(resolve, 100));
        waitCount++;
      }
      console.log(`[handleStopRecording] Auto-chunk wait completed after ${waitCount * 100}ms`);
    }

    // Prevent double-click or rapid calls - check state early and set recording to false immediately
    const recorderState = mediaRecorderRef.current?.state;
    console.log(`[handleStopRecording] State check - isStoppingRef=${isStoppingRef.current}, recorderExists=${!!mediaRecorderRef.current}, recorderState=${recorderState}`);
    if (isStoppingRef.current || !mediaRecorderRef.current || mediaRecorderRef.current.state === 'inactive') {
      console.log('[handleStopRecording] Early return - already stopping or inactive');
      return;
    }
    isStoppingRef.current = true;

    // Stop chunk timer (prevent auto-chunk during final stop)
    stopChunkTimer();

    // Stop timer and set recording to false immediately to prevent UI freeze
    stopTimer();
    setIsRecording(false);

    // Calculate actual chunk duration from chunk start time (not segment timer which runs continuously)
    const recordedDuration = chunkStartTimeRef.current
      ? Math.round((Date.now() - chunkStartTimeRef.current) / 1000)
      : segmentTimeRef.current;
    const currentInspection = inspectionRef.current;
    const mimeType = mimeTypeRef.current;
    // Capture timestamp NOW (before async) so it's available for pending segment tracking
    const segmentTimestamp = Date.now();

    console.log(`[handleStopRecording] Chunk duration: ${recordedDuration}s (from chunkStartTime)`);

    // Process audio in background after recorder stops
    mediaRecorderRef.current.addEventListener('stop', async () => {
      const rawBlob = new Blob(audioChunksRef.current, { type: mimeType });

      // Clear chunks immediately to free memory
      audioChunksRef.current = [];

      if (rawBlob.size === 0) {
        console.error('Recording is empty');
        // Still call onStopRecording to return to VehicleInput
        onStopRecording({
          inspectionId: currentInspection?.inspectionId,
          vehicleId: vehicleId,
          duration: 0,
          timestamp: segmentTimestamp,
        });
        return;
      }

      // Skip storing zero-duration chunks (can happen if stop is called right after auto-chunk)
      if (recordedDuration <= 0) {
        console.log('[handleStopRecording] Skipping zero-duration chunk');
        onStopRecording({
          inspectionId: currentInspection?.inspectionId,
          vehicleId: vehicleId,
          duration: 0,
          timestamp: segmentTimestamp,
        });
        return;
      }

      // Store RAW chunk to IndexedDB IMMEDIATELY (before any trimming)
      // This ensures the chunk appears in VehicleInput table right away
      if (currentInspection) {
        const stats = await getSegmentStats(currentInspection.inspectionId);
        const chunkIndex = stats.total;

        // Store raw blob to IndexedDB (fast, < 100ms) with PENDING status
        const pendingChunkId = await storeChunk({
          recordingId: currentInspection.inspectionId,
          chunkIndex,
          blob: rawBlob,
          mimeType: mimeType || 'audio/webm',
          duration: recordedDuration,
          timestamp: segmentTimestamp
        });
        console.log('Segment stored to IndexedDB with timestamp:', segmentTimestamp, 'chunkId:', pendingChunkId);

        // Return to VehicleInput IMMEDIATELY after chunk is stored
        onStopRecording({
          inspectionId: currentInspection.inspectionId,
          vehicleId: vehicleId,
          duration: recordedDuration,
          timestamp: segmentTimestamp,
        });

        // Do trimming and upload in background (don't block UI)
        (async () => {
          let audioBlob = rawBlob;

          // Debug: Log startTriggerInfo to diagnose trimming issues
          console.log(`[StartTrim] startTriggerInfo:`, JSON.stringify(startTriggerInfo));
          console.log(`[StartTrim] recordingStartTimeRef.current:`, recordingStartTimeRef.current);
          console.log(`[StartTrim] Condition: timestamp=${startTriggerInfo?.timestamp}, startTime=${recordingStartTimeRef.current}, willTrim=${!!(startTriggerInfo?.timestamp && recordingStartTimeRef.current)}`);

          // If voice start triggered, trim the beginning to remove start phrase
          if (startTriggerInfo?.timestamp && recordingStartTimeRef.current) {
            const detectedPhrase = startTriggerInfo.phrase || voiceTriggerService.getTriggerWord();
            const skipMs = voiceTriggerService.estimatePhraseDurationMs(detectedPhrase);
            try {
              console.log(`[StartTrim] Trimming start of audio by ${skipMs}ms (detected phrase: "${detectedPhrase}")`);
              audioBlob = await trimAudioStart(audioBlob, skipMs);
              console.log(`[StartTrim] After trim: size changed from ${rawBlob.size} to ${audioBlob.size}`);
            } catch (err) {
              console.error('[StartTrim] Start phrase trimming failed, using original:', err);
            }
          } else {
            console.log(`[StartTrim] Skipping start trim - condition not met`);
          }

          // If voice stop triggered with trim info, trim the audio end
          // Try multiple sources for trim value (mobile may not propagate function params correctly)
          const onstopTime = Date.now();
          console.log(`[TrimDebug] onstop handler - trimToMs=${trimToMs}, pendingTrimMsRef.current=${pendingTrimMsRef.current}, voiceStopInfoRef.current=${JSON.stringify(voiceStopInfoRef.current)}`);
          setDebugInfo(prev => ({
            ...prev,
            onstopTime,
            trimToMs,
            pendingTrimMs: pendingTrimMsRef.current,
            voiceStopInfo: voiceStopInfoRef.current ? { ...voiceStopInfoRef.current } : null
          }));
          let effectiveTrimMs = trimToMs ?? pendingTrimMsRef.current;

          // Third fallback: recalculate from voiceStopInfoRef if available
          // NOTE: On mobile, the hook writes to this ref SYNCHRONOUSLY before the callback runs,
          // so it may only have detectionTimestamp and detectedPhrase (no recordingStartTime).
          // Fall back to recordingStartTimeRef.current if needed.
          if (effectiveTrimMs === null && voiceStopInfoRef.current) {
            console.log(`[TrimCheck] Recalculating from voiceStopInfoRef:`, JSON.stringify(voiceStopInfoRef.current));
            const { detectionTimestamp, detectedPhrase } = voiceStopInfoRef.current;
            // Use recordingStartTime from voiceStopInfoRef if available, else fall back to ref
            const recordingStartTime = voiceStopInfoRef.current.recordingStartTime || recordingStartTimeRef.current;
            if (detectionTimestamp && recordingStartTime) {
              const stopPhraseBufferMs = voiceTriggerService.estimatePhraseDurationMs(detectedPhrase);
              const EXTRA_BUFFER_MS = 1500;
              const keepDurationMs = detectionTimestamp - recordingStartTime - stopPhraseBufferMs - EXTRA_BUFFER_MS;
              effectiveTrimMs = keepDurationMs > 500 ? keepDurationMs : null;
              console.log(`[TrimCheck] Recalculated effectiveTrimMs=${effectiveTrimMs}`);
            }
          }

          console.log(`[TrimCheck] trimToMs=${trimToMs}, pendingTrimMsRef=${pendingTrimMsRef.current}, effectiveTrimMs=${effectiveTrimMs}`);
          console.log(`[TrimCheck] BEFORE trim: audioBlob.size=${audioBlob.size}`);

          // Clear the refs after reading
          pendingTrimMsRef.current = null;
          voiceStopInfoRef.current = null;

          if (effectiveTrimMs !== null && effectiveTrimMs > 0) {
            try {
              console.log(`Trimming audio to ${effectiveTrimMs}ms (removing stop word)`);
              const trimmedBlob = await trimAudioEnd(audioBlob, effectiveTrimMs);
              console.log(`[TrimCheck] AFTER trim: trimmedBlob.size=${trimmedBlob.size}, original=${audioBlob.size}`);
              audioBlob = trimmedBlob;
            } catch (err) {
              console.error('Audio trimming failed, using original:', err);
            }
          }
          console.log(`[TrimCheck] Final audioBlob.size=${audioBlob.size} being passed to processAndUploadSegment`);

          // Upload (with trimmed blob) - processAndUploadSegment updates the chunk in-place
          processAndUploadSegment(
            currentInspection.inspectionId,
            audioBlob,
            (status) => console.log('Upload status:', status),
            pendingChunkId
          ).then(result => {
            if (result.status === 'uploaded') {
              console.log('Background upload complete:', result.filename);
            }
          }).catch(err => {
            console.error('Background upload failed (will retry later):', err);
          });
        })();
      } else {
        // No inspection - return to VehicleInput
        onStopRecording({
          vehicleId: vehicleId,
          duration: recordedDuration,
          timestamp: segmentTimestamp,
        });
      }
    }, { once: true });

    playStopBeep();
    if (settings.vibrateOnRecording.isEnabled() && navigator.vibrate) {
      navigator.vibrate([100, 50, 100]);
    }

    mediaRecorderRef.current.stop();
    stopMediaStream();
    stopAudioAnalysis();
  };

  // Keep ref in sync for voice stop callback (avoids stale closure)
  handleStopRecordingRef.current = handleStopRecording;
  // Debug: confirm ref is assigned (only log once per mount)
  if (!handleStopRecordingRef._logged) {
    console.log('[Recording] handleStopRecordingRef assigned');
    handleStopRecordingRef._logged = true;
  }

  const handleCancel = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    stopTimer();
    stopMediaStream();
    stopAudioAnalysis();
    onCancel();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <header className="app-bar">
          <button onClick={onHome || onCancel} className="cursor-pointer hover:opacity-80">
            <Logo size={40} />
          </button>
          <h1 className="flex-1 text-white font-normal text-xl text-center">{startTriggerInfo?.title || vehicleId || t('recording.new_inspection')}</h1>
          {onLogout && <AppBarMenu onLogout={onLogout} isAdmin={isAdmin} />}
        </header>
        <main className="w-full px-4 lg:px-8 py-8">
          {/* Inspection Title OR Vehicle ID - not both */}
          {startTriggerInfo?.title ? (
            <p className="text-sm font-bold text-black text-center mb-4 tracking-wider">
              {startTriggerInfo.title}
            </p>
          ) : vehicleId ? (
            <p className="text-sm font-bold font-mono tracking-wider text-center mb-4">
              {vehicleId.toUpperCase()}
            </p>
          ) : null}
          <Alert severity="error">{error}</Alert>
          <button onClick={onHome || handleCancel} className="btn-primary w-full mt-4">
            {t('common.back')}
          </button>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* App Bar */}
      <header className="app-bar">
        <button onClick={onHome || onCancel} className="cursor-pointer hover:opacity-80">
          <Logo size={40} />
        </button>
        <h1 className="flex-1 text-white font-normal text-xl text-center">{startTriggerInfo?.title || vehicleId || t('recording.new_inspection')}</h1>
        {onLogout && <AppBarMenu onLogout={onLogout} isAdmin={isAdmin} />}
      </header>

      {/* Main Content */}
      <main className="w-full px-4 lg:px-8 py-8">
        <div className="card-elevated p-6 text-center">
          {/* Timer */}
          <div className="mb-6">
            <span className="font-mono font-bold text-5xl text-secondary">
              {formatTime(displayTime)}
            </span>
          </div>

          {/* Audiogram */}
          <div className="mb-8">
            <Audiogram analyser={analyser} barCount={64} className="h-32" />

            {/* Inspection Title OR Vehicle ID - not both */}
            {startTriggerInfo?.title ? (
              <p className="text-sm font-bold text-black text-center mt-3 tracking-wider">
                {startTriggerInfo.title}
              </p>
            ) : vehicleId ? (
              <p className="text-sm font-bold font-mono tracking-wider text-center mt-3 mb-1">
                {vehicleId.toUpperCase()}
              </p>
            ) : null}

            {inputDeviceName && (
              <p className="text-xs text-text-muted text-center mt-2">
                {inputDeviceName}
              </p>
            )}
            {wakeLockActive && (
              <p className="text-xs text-green-600 text-center mt-1">
                {t('recording.screen_lock_active')}
              </p>
            )}
            {/* Chunk duration indicator */}
            <p className="text-xs text-text-muted text-center mt-1">
              {t('recording.auto_chunk_info', { duration: formatTime(settings.chunkDuration.get()) })}
            </p>
          </div>

          {/* Voice Stop Trigger Indicator */}
          {stopTriggerEnabled && isRecording && (
            <div className={`mb-4 px-4 py-2 rounded-lg flex flex-col gap-1 ${
              isVoiceStopModelLoading
                ? 'bg-blue-50 border border-blue-200'
                : voiceRetryCount >= 3 && !useVoskForStopTrigger
                  ? 'bg-amber-50 border border-amber-200'
                  : voiceStopListening
                    ? 'bg-green-50 border border-green-200'
                    : 'bg-gray-50 border border-gray-200'
            }`}>
              {/* Debug info for Vosk troubleshooting - only when Extended Error Reports enabled */}
              {extendedErrorReportsEnabled && useVoskForStopTrigger && (
                <div className="text-xs text-gray-500 font-mono">
                  ctx:{voskAudioContext ? '✓' : '✗'} src:{voskSourceNode ? '✓' : '✗'} listen:{voskStopListening ? '✓' : '✗'}
                </div>
              )}
              <div className="flex items-center gap-2">
              <Mic size={18} className={`${
                isVoiceStopModelLoading
                  ? 'text-blue-600 animate-spin'
                  : voiceRetryCount >= 3 && !useVoskForStopTrigger
                    ? 'text-amber-600'
                    : voiceStopListening
                      ? 'text-green-600 animate-pulse'
                      : 'text-gray-400'
              }`} />
              <span className={`text-sm ${
                isVoiceStopModelLoading
                  ? 'text-blue-800'
                  : voiceRetryCount >= 3 && !useVoskForStopTrigger
                    ? 'text-amber-800'
                    : voiceStopListening
                      ? 'text-green-800'
                      : 'text-gray-600'
              }`}>
                {isVoiceStopModelLoading
                  ? t('recording.voice_model_loading', { percent: voskModelLoadProgress || 0 })
                  : voiceRetryCount >= 3 && !useVoskForStopTrigger
                    ? t('recording.voice_stop_reconnecting')
                    : t('recording.voice_stop_listening', { phrase: voiceTriggerService.getStopWord() })}
              </span>
              {useVoskForStopTrigger && (
                <span className="text-xs text-gray-500 ml-auto">Vosk</span>
              )}
              </div>
            </div>
          )}

          {/* Stop Button */}
          <button
            onClick={() => handleStopRecording()}
            disabled={!isRecording}
            className="w-full bg-primary hover:bg-primary-dark active:scale-[0.98] disabled:opacity-60 text-white font-bold text-2xl py-8 px-6 rounded-lg flex flex-col items-center gap-3 transition-colors cursor-pointer"
          >
            <Square size={64} fill="currentColor" />
            <span>{t('recording.stop')}</span>
          </button>

          {/* Voice Debug Log - only visible when Extended Error Reports enabled */}
          {extendedErrorReportsEnabled && stopTriggerEnabled && voiceDebugLog.length > 0 && (
            <div className="mt-4 p-3 bg-gray-900 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  {t('recording.voice_debug_log')}
                </h3>
                <button
                  type="button"
                  onClick={() => setVoiceDebugLog([])}
                  className="text-xs text-gray-500 hover:text-gray-300"
                >
                  {t('common.clear')}
                </button>
              </div>
              <div className="max-h-48 overflow-y-auto font-mono text-xs space-y-1">
                {voiceDebugLog.map(entry => (
                  <div
                    key={entry.id}
                    className={`flex items-start gap-2 ${
                      entry.matchedKeyword ? 'text-red-400 font-bold' : 'text-green-400'
                    }`}
                  >
                    <span className="text-gray-500 flex-shrink-0">[{entry.timestamp}]</span>
                    <span className="flex-1">
                      "{entry.transcript}"
                      {entry.confidence && <span className="text-gray-500 ml-2">({entry.confidence})</span>}
                      {entry.matchedKeyword && <span className="text-yellow-300 ml-2">→ MATCH: "{entry.matchedKeyword}"</span>}
                    </span>
                  </div>
                ))}
              </div>
              {voiceDebugLog.length > 0 && voiceDebugLog[0].keywords && (
                <div className="mt-2 pt-2 border-t border-gray-700 text-xs text-gray-500">
                  {t('recording.voice_keywords')}: {voiceDebugLog[0].keywords}
                </div>
              )}
            </div>
          )}

          {/* Debug Panel - shows trim info for mobile debugging */}
          {debugInfo && (
            <div className="mt-4 p-3 bg-gray-100 rounded text-xs font-mono text-left overflow-auto max-h-48">
              <div className="font-bold mb-1">Debug Info:</div>
              <div>hookWroteRef: {debugInfo.hookWroteRef ? 'YES' : (debugInfo.hookWroteRef === false ? 'NO' : '-')}</div>
              <div>callback: {debugInfo.callbackCalled ? 'YES' : '-'}</div>
              <div>trimToMs: {debugInfo.trimToMs ?? 'null'}</div>
              <div>pendingTrimMs: {debugInfo.pendingTrimMs ?? 'null'}</div>
              <div>voiceStopInfo: {debugInfo.voiceStopInfo ? JSON.stringify(debugInfo.voiceStopInfo) : 'null'}</div>
              {debugInfo.hookWriteTime && debugInfo.onstopTime && (
                <div className="text-green-700">hook-&gt;onstop: {debugInfo.onstopTime - debugInfo.hookWriteTime}ms</div>
              )}
              {debugInfo.warning && <div className="text-red-600">WARNING: {debugInfo.warning}</div>}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Recording;
